/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    :class="isGroupManager ? 'row' : 'row showBackground'"
  >
    <div class="get-started-left-col col-sm-5">
      <h1>Many easy ways to get started</h1>
      <p>
        You can upload your first speech using any of the following four options:
        <ul>
          <li>Use the <strong>Record</strong> tab to record a speech right from your browser</li>
          <li>Use the <strong>Upload</strong> tab to upload a pre-recorded audio file</li>
          <li>Use the <strong>URL</strong> tab to upload a speech from a webpage, such as YouTube</li>
          <li>
            Download the <strong>VoiceVibes Recorder App</strong> for your Apple smartphone.
            It's available <strong>for free</strong> on the App Store:
            <p class="sr-only">
              Please note that users who are reliant on screen readers are recommended to use
              the recording functionality on the Record tab on this website, instead of
              installing and using the VoiceVibes mobile app. All functionality provided by
              our app is also available on this website, which has been tailored to provide the
              best experience possible to visitors utilizing screen readers.
            </p>
            <br>
            <div>
              <a
                href="https://itunes.apple.com/us/app/voicevibes-recorder/id1014688052?mt=8"
                target="_blank"
              >
                <img
                  alt="Get it on Apple App Store"
                  height="34"
                  style="margin: 20px;"
                  src="https://voicevibes.s3.amazonaws.com/static/images/iphone_App_Store_Badge.svg"
                >
              </a>
            </div>
          </li>
        </ul>
      </p>
      <h2>Getting Started</h2>
      <p>
        To learn more about all the functionality available in your VoiceVibes account,
        you can refer to the following document(s):
        <ul>
          <li v-if="isOrgLevelAdmin">
            <a
              :href="assignmentsEnabled ? 'https://s3-us-west-2.amazonaws.com/voicevibes/resources/Getting-Started-For-Administrators-Assignments-On.pdf' : 'https://s3-us-west-2.amazonaws.com/voicevibes/resources/Getting-Started-For-Administrators.pdf'"
              target="_blank"
            >
              Getting Started For Admins
            </a>
          </li>
          <li v-if="isGroupManager">
            <a
              :href="assignmentsEnabled ? 'https://s3-us-west-2.amazonaws.com/voicevibes/resources/Getting-Started-For-Coaches.pdf' : 'https://s3-us-west-2.amazonaws.com/voicevibes/resources/Getting-Started-For-Managers.pdf'"
              target="_blank"
            >
              Getting Started For Coaches
            </a>
          </li>
          <li v-if="isSpeaker">
            <a
              :href="assignmentsEnabled ? 'https://s3-us-west-2.amazonaws.com/voicevibes/resources/Getting-Started-For-Speakers.pdf' : 'https://s3-us-west-2.amazonaws.com/voicevibes/resources/Getting-Started-Handout.pdf'"
              target="_blank"
            >
              Getting Started For Speakers
            </a>
          </li>
        </ul>
      </p>
      <h2>Trouble Recording?</h2>
      Please review
      <a
        target="_blank"
        href="https://voicevibes.s3-us-west-2.amazonaws.com/resources/Troubleshooting-Recording-Errors-in-VoiceVibes.pdf"
      >
        Troubleshooting Recording Errors in VoiceVibes
      </a>
      <h2>Change your Account Settings</h2>
      <p>
        Click to view the <router-link :to="{ name: 'profile' }">
          Account Settings Page
        </router-link>
      </p>
      <h2>Contact Us</h2>
      <p>
        We're here to help! Contact our support via <a href="mailto:support@bigtincan.com">
          support@bigtincan.com
        </a>
      </p>
      <h2>More questions?</h2>
      <p>
        Read our <a
          href="http://www.myvoicevibes.com/product-qa.html"
          target="_blank"
        >
          Frequently Asked Questions
        </a>
      </p>
    </div>
    <div class="get-started-graphic col-sm-6">
      <div v-if="!assignmentsEnabled && isGroupManager">
        <h3>Getting Started Video</h3>
        <video
          width="640"
          height="480"
          controls
        >
          <source
            src="https://voicevibes.s3-us-west-2.amazonaws.com/resources/coaches.mp4"
            type="video/mp4"
          >
        </video>
      </div>
      <div v-else-if="assignmentsEnabled && !isGroupManager">
        <h3>Getting Started with VoiceVibes</h3>
        <video
          width="640"
          height="480"
          controls
        >
          <source
            src="https://voicevibes.s3-us-west-2.amazonaws.com/resources/assignment-speakers.mp4"
            type="video/mp4"
          >
        </video>
      </div>
      <div v-else-if="assignmentsEnabled && isGroupManager">
        <h3>Coach Tutorial 1: Creating Assignments</h3>
        <video
          width="640"
          height="480"
          controls
        >
          <source
            src="https://voicevibes.s3-us-west-2.amazonaws.com/resources/assignment-coaches1.mp4"
            type="video/mp4"
          >
        </video>
        <h3>Coach Tutorial 2: Managing Your Team</h3>
        <video
          width="640"
          height="480"
          controls
        >
          <source
            src="https://voicevibes.s3-us-west-2.amazonaws.com/resources/assignment-coaches2.mp4"
            type="video/mp4"
          >
        </video>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .showBackground {
    position: relative;
    background:
        url(https://voicevibes.s3.amazonaws.com/static/images/waves_divider_full_color.svg) 0px 200px no-repeat,
        url(https://voicevibes.s3.amazonaws.com/static/images/Bigtincan_art.jpg) 0px 130px no-repeat;
    background-size: 100%;
  }
  .get-started-left-col {
    background-color: white;
  }
</style>

<script>
import BasePage from '../pages/BasePage';

export default {
  mixins: [BasePage],
  data() {
    return {};
  },
  computed: {
    assignmentsEnabled() {
      return (this.$store.getters.org && typeof this.$store.getters.org.settings !== 'undefined' && this.$store.getters.org.settings.Assignments);
    },
  },
};
</script>
